import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowLeft, ShieldCheck, Upload, CreditCard, Truck } from "lucide-react";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicLayout from "@/components/public/PublicLayout";
import { ProductDescription } from "@/components/ProductDescription";
import { ProductSpecs } from "@/components/ProductSpecs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function FileField({ label, testid, onUploaded, privateFile }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext) || (file.type && !["image/jpeg", "image/png"].includes(file.type))) {
      toast.error("Only JPG, JPEG or PNG images are allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large — maximum size is 10MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/uploads?private=${privateFile ? "true" : "false"}`, fd);
      onUploaded(r.data.id);
      setDone(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border p-3 text-sm hover:border-primary" data-testid={testid}>
      {done ? <Check className="h-4 w-4 text-primary" /> : <Upload className="h-4 w-4 text-muted-foreground" />}
      <span className="text-muted-foreground">{busy ? "Uploading..." : done ? `${label} uploaded` : label}</span>
      <input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" className="hidden" onChange={upload} />
    </label>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [calc, setCalc] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ customer_name: "", mobile: "", email: "", cnic: "", address: "", city: "", occupation: "", monthly_income: "", notes: "", photo_id: "", cnic_front_id: "", cnic_back_id: "" });

  useEffect(() => {
    let active = true;
    api.get("/public/settings").then((r) => { if (active) setSettings(r.data); }).catch(() => {});
    (async () => {
      try {
        const pr = await api.get(`/public/products/${id}`);
        if (!active) return;
        setProduct(pr.data);
        let eff = (pr.data.plans && pr.data.plans.length) ? pr.data.plans : [];
        if (!eff.length) {
          try { const gp = await api.get("/public/plans"); eff = gp.data || []; } catch { eff = []; }
        }
        if (!active) return;
        setPlans(eff);
        setSelectedPlan((prev) => (prev && eff.some((p) => p.id === prev.id) ? prev : (eff[0] || null)));
      } catch { if (active) navigate("/products"); }
    })();
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (!product || !selectedPlan) { setCalc(null); return; }
    const price = Number(product.installment_price) || 0;
    const plan = selectedPlan;
    const months = Number(plan.duration_months) || 0;
    const advance = Math.round(price * (Number(plan.advance_percent) || 0) / 100);
    const business = plan.business_charge_percent != null
      ? Math.round(price * (Number(plan.business_charge_percent) || 0) / 100)
      : (Number(plan.business_charges) || 0);
    const total = price + business;
    const monthly = months ? Math.round((total - advance) / months) : 0;
    setCalc({ main_price: price, duration_months: months, advance_payment: advance, monthly_installment: monthly, total_payable: total });
  }, [product, selectedPlan]);

  useEffect(() => {
    if (user && user.role === "customer") {
      setForm((f) => ({ ...f, customer_name: user.name || "", email: user.email || "", mobile: user.mobile || "", cnic: user.cnic || "", address: user.address || "", city: user.city || "" }));
    }
  }, [user]);

  const symbol = settings?.currency?.symbol || "Rs.";

  const onApply = () => {
    if (!user) { navigate(`/login?next=/products/${id}`); return; }
    if (user.role !== "customer") { toast.info("Staff accounts cannot place installment requests."); return; }
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/requests", { ...form, product_id: product.id, plan_id: selectedPlan.id, advance_payment: calc?.advance_payment });
      toast.success(`Request ${r.data.request_no} submitted!`);
      setShowForm(false);
      navigate("/portal/requests");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setSubmitting(false);
  };

  if (!product) return <PublicLayout><div className="py-32 text-center text-muted-foreground">Loading...</div></PublicLayout>;
  const gallery = (product.images || []).filter(Boolean);
  if (!gallery.length) gallery.push("https://images.unsplash.com/photo-1526406915894-7bcd65f60845");

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-6 py-8">
        <Link to="/products" className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"><ArrowLeft className="h-4 w-4" />Back to Products</Link>
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <div className="overflow-hidden rounded-2xl border border-border bg-secondary">
              <img src={gallery[imgIdx]} alt={product.name} className="aspect-square w-full object-cover" data-testid="product-image" />
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 flex gap-3" data-testid="product-thumbnails">
                {gallery.map((g, k) => (
                  <button key={k} onClick={() => setImgIdx(k)} data-testid={`thumb-${k}`}
                    className={`h-16 w-16 overflow-hidden rounded-lg border-2 transition-colors ${k === imgIdx ? "border-primary" : "border-border"}`}>
                    <img src={g} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <span className="overline text-primary">{product.brand} · {product.model}</span>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl" data-testid="product-name">{product.name}</h1>
            <div className="mt-4 flex items-baseline gap-3">
              <p className="text-3xl font-extrabold text-primary" data-testid="product-price">{money(calc ? calc.total_payable : product.installment_price, symbol)}</p>
              <p className="text-muted-foreground line-through">{money(product.cash_price, symbol)} cash</p>
            </div>
            <div className="mt-4"><ProductDescription template={product.description_template} text={product.description} /></div>
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4 text-primary" />{product.warranty} · {product.stock > 0 ? "In Stock" : "Out of Stock"}</div>

            <div className="mt-6">
              <Label className="overline text-muted-foreground">Select Installment Plan</Label>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="plan-selector">
                {plans.map((p) => (
                  <button key={p.id} onClick={() => setSelectedPlan(p)} data-testid={`plan-${p.duration_months}`}
                    className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${selectedPlan?.id === p.id ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>
                    {p.duration_months} Months
                  </button>
                ))}
              </div>
            </div>

            {calc && (
              <div className="mt-6 rounded-xl border border-border bg-card p-5" data-testid="calc-box">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <p className="overline text-muted-foreground">Installment Plan</p>
                    <p className="mt-1 font-display text-lg font-bold" data-testid="calc-plan-name">{calc.duration_months} Months</p>
                  </div>
                  <div>
                    <p className="overline text-muted-foreground">Advance Payment</p>
                    <p className="mt-1 font-display text-lg font-bold" data-testid="calc-advance">{money(calc.advance_payment, symbol)}</p>
                  </div>
                  <div>
                    <p className="overline text-muted-foreground">Monthly Installment</p>
                    <p className="mt-1 font-display text-xl font-extrabold text-primary" data-testid="calc-monthly">{money(calc.monthly_installment, symbol)}<span className="text-sm font-medium text-muted-foreground"> / month</span></p>
                  </div>
                </div>
              </div>
            )}

            <Button onClick={onApply} size="lg" className="mt-6 w-full rounded-full" data-testid="apply-installment-btn" disabled={product.stock === 0}>
              {product.stock === 0 ? "Out of Stock" : "Book Now / Apply for Installment"}
            </Button>

            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div className="mt-8">
                <h3 className="mb-3 font-display font-semibold">Specifications</h3>
                <ProductSpecs design={product.specs_design} specs={product.specifications} />
              </div>
            )}
          </div>
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-6 pb-10" data-testid="product-benefits">
        <div className="grid gap-4 sm:grid-cols-3">
          {[[ShieldCheck, "Brand Warranty", "Official manufacturer warranty"], [CreditCard, "Easy Buy / Easy Pay", "Flexible easy monthly installments"], [Truck, "Fast & Free Delivery", "Quick delivery across Pakistan"]].map(([Icon, t, d], k) => (
            <div key={k} className="flex items-center gap-3 rounded-xl border border-border bg-card p-5">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground"><Icon className="h-6 w-6" /></span>
              <div><p className="font-display font-semibold">{t}</p><p className="text-xs text-muted-foreground">{d}</p></div>
            </div>
          ))}
        </div>
      </section>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Installment Request — {product.name}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="request-form">
            <div><Label>Full Name</Label><Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="req-name" /></div>
            <div><Label>Mobile Number</Label><Input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="req-mobile" /></div>
            <div><Label>Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="req-email" /></div>
            <div><Label>CNIC</Label><Input required value={form.cnic} onChange={(e) => setForm({ ...form, cnic: e.target.value })} placeholder="35201-1234567-1" data-testid="req-cnic" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="req-address" /></div>
            <div><Label>City</Label><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="req-city" /></div>
            <div><Label>Occupation</Label><Input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} /></div>
            <div><Label>Monthly Income</Label><Input value={form.monthly_income} onChange={(e) => setForm({ ...form, monthly_income: e.target.value })} /></div>
            <div><Label>Advance</Label><Input value={money(calc?.advance_payment, symbol)} disabled /></div>
            <div className="sm:col-span-2"><Label>Additional Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="sm:col-span-2 grid gap-2">
              <Label>Documents (optional)</Label>
              <FileField label="Customer Photo" testid="upload-photo" privateFile onUploaded={(fid) => setForm((f) => ({ ...f, photo_id: fid }))} />
              <FileField label="CNIC Front" testid="upload-cnic-front" privateFile onUploaded={(fid) => setForm((f) => ({ ...f, cnic_front_id: fid }))} />
              <FileField label="CNIC Back" testid="upload-cnic-back" privateFile onUploaded={(fid) => setForm((f) => ({ ...f, cnic_back_id: fid }))} />
            </div>
            <Button type="submit" className="sm:col-span-2 rounded-full" disabled={submitting} data-testid="submit-request-btn">{submitting ? "Submitting..." : "Submit Request"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}
